import BillingContactExternalForm from 'components/billingContacts/BillingContactExternalForm';
import {
  billingContactFormFrom,
  toBillingContactPayload,
  validateBillingContactForm,
} from 'components/billingContacts/billingContactForm';
import Button from 'components/crm/ui/Button';
import Modal from 'components/crm/ui/Modal';
import { apiErrorText, errorCodeOf } from 'helper/apiErrorText';
import { LEAD_STATUSES } from 'components/SlideOver/dataSlideOver/data/slideOverCompany/CompanyContactEditView';
import StyledInput from 'components/styledInput/StyledInput';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

// Mounted while open only, so that each opening starts from the person as shown.
const ContactEditForm = ({ contactId, person, onClose, onSaved }) => {
  const { text } = useText('record');
  const { language } = UseContext();
  const { call } = useCrmContext();
  const [form, setForm] = useState(() => billingContactFormFrom(person));
  const [leadStatus, setLeadStatus] = useState(person.leadStatus ?? '');
  const [errors, setErrors] = useState({});
  // A refusal of the address keeps the form open, its text under the field.
  const [emailTaken, setEmailTaken] = useState('');
  const [busy, setBusy] = useState(false);

  const changeField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
    if (field === 'email') setEmailTaken('');
  };

  const save = () => {
    // The add contact modal's checks, without its position: the notification
    // boxes are a billing contact's, never asked here.
    const { notifications, ...found } = validateBillingContactForm(form);
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    const { firstname, lastname, email, phone } = toBillingContactPayload(form);
    setBusy(true);
    call(crmApi.saveContact, { contactId, firstname, lastname, email, phone: phone ?? '' }).then(
      async () => {
        // The lead status is the CRM's, written after the person, as on a
        // member's « Modifier le contact » (CompanyContactEditView).
        if (leadStatus !== (person.leadStatus ?? '')) {
          try {
            await call(crmApi.setLeadStatus, { personId: contactId, status: leadStatus });
          } catch {
            toast.error('Contact enregistré, mais son statut du lead n’a pas été modifié.');
            onSaved();
            return;
          }
        }
        toast.success(text('contactEditSaved'));
        onSaved();
      },
      (error) => {
        setBusy(false);
        if (error?.name === 'CanceledError') return;
        if (errorCodeOf(error) === 'CONTACT_EMAIL_TAKEN') {
          setEmailTaken(text('contactEditErrors.CONTACT_EMAIL_TAKEN'));
          return;
        }
        toast.error(apiErrorText(error, { language, text, group: 'contactEditErrors' }));
      },
    );
  };

  const fieldErrors = Object.fromEntries(
    Object.entries(errors)
      .filter(([, key]) => key)
      .map(([field, key]) => [field, language.getValue(`companyBillingContacts.errors.${key}`, '')]),
  );
  if (emailTaken) fieldErrors.email = emailTaken;

  return (
    <Modal
      open
      onClose={busy ? undefined : onClose}
      title={text('contactEditTitle')}
      description={text('contactEditDescription')}
      width={480}
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={onClose}>
            {text('contactEditCancel')}
          </Button>
          <Button disabled={busy} aria-busy={busy || undefined} onClick={save}>
            {text(busy ? 'contactEditSaving' : 'contactEditSave')}
          </Button>
        </>
      }
    >
      <BillingContactExternalForm
        form={form}
        errors={fieldErrors}
        onChange={changeField}
        isDisabled={busy}
        withNotifications={false}
      />
      <StyledInput
        type={'select'}
        label={text('fieldLead')}
        value={leadStatus}
        isDisabled={busy}
        list={LEAD_STATUSES.map((status) => ({ value: status, label: text(`lead_${status || 'none'}`) }))}
        onChange={(event) => setLeadStatus(event.target.value)}
      />
    </Modal>
  );
};

/**
 * « Modifier les informations » of a person without an account (JBWEB-1431):
 * first name, last name, phone and e-mail, written by the api in every
 * company and group of that person (admin/crm/contact/save), then their lead
 * status (admin/crm/person/lead). `contactId`: one
 * of their contacts, the page's; `onSaved` once the api has written them.
 */
const ContactEditDialog = ({ open, ...props }) => (open ? <ContactEditForm {...props} /> : null);

export default ContactEditDialog;
